import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, CheckCircle, XCircle, Lock } from 'lucide-react';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || !confirmPassword) { toast.error('Please fill in all fields'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }

    setLoading(true);
    setError('');
    try {
      await apiClient.post('/auth/reset-password', { token, new_password: password });
      setSuccess(true);
      toast.success('Password reset successful!');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to reset password';
      setError(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'bg-white border-stone-300 focus:border-[#EFB342] focus:ring-1 focus:ring-[#EFB342] text-stone-900 placeholder:text-stone-400 h-14 text-base lg:text-lg';

  // Success View
  if (success) {
    return (
      <div className="min-h-screen bg-[#F5F4F1] flex items-center justify-center p-8 text-stone-900">
        <div className="w-full max-w-md text-center">
          <div className="w-20 h-20 bg-[#EFB342]/20 border border-[#EFB342]/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-[#B8871E]" size={40} />
          </div>
          <p className="text-[#EFB342] uppercase tracking-[0.2em] text-xs font-semibold mb-3">All set</p>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-4 uppercase tracking-tight">Password reset.</h1>
          <p className="text-stone-600 text-base md:text-lg mb-8 leading-relaxed">
            Your password has been successfully reset. You can now log in with your new password.
          </p>
          <Button
            onClick={() => navigate('/login')}
            className="bg-[#EFB342] text-black hover:bg-[#D99F30] font-bold uppercase tracking-wider h-14 text-base lg:text-lg px-8 rounded-full"
            data-testid="reset-success-login-btn"
          >
            Go to login
          </Button>
        </div>
      </div>
    );
  }

  // Error View (invalid token)
  if (error && !token) {
    return (
      <div className="min-h-screen bg-[#F5F4F1] flex items-center justify-center p-8 text-stone-900">
        <div className="w-full max-w-md text-center">
          <div className="w-20 h-20 bg-red-100 border border-red-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="text-red-500" size={40} />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-4 uppercase tracking-tight">Invalid link</h1>
          <p className="text-stone-600 text-base md:text-lg mb-8 leading-relaxed">{error}</p>
          <Button
            onClick={() => navigate('/login')}
            className="bg-[#EFB342] text-black hover:bg-[#D99F30] font-bold uppercase tracking-wider h-14 text-base lg:text-lg px-8 rounded-full"
            data-testid="reset-error-back-btn"
          >
            Back to login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F4F1] flex items-center justify-center p-8 text-stone-900">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 mb-10 justify-center" data-testid="reset-brand-logo">
          <div className="w-11 h-11 bg-[#EFB342] rounded-xl flex items-center justify-center">
            <span className="text-black font-heading font-bold text-xl">Rx</span>
          </div>
          <div>
            <h1 className="font-heading text-xl font-bold text-stone-900 tracking-wide leading-none">RxGRIT</h1>
            <p className="text-[10px] text-stone-500 uppercase tracking-widest mt-1">by BUILD Nutrition</p>
          </div>
        </Link>

        <div className="mb-8 text-center">
          <div className="w-16 h-16 bg-[#EFB342]/20 border border-[#EFB342]/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="text-[#B8871E]" size={30} />
          </div>
          <p className="text-[#EFB342] uppercase tracking-[0.2em] text-xs font-semibold mb-3">Password reset</p>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-stone-900 mb-3 uppercase tracking-tight">Create a new password</h2>
          <p className="text-stone-600 text-base leading-relaxed">Enter your new password below.</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6">
            <p className="text-red-600 text-center text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="password" className="text-stone-700 text-base">New password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`${inputCls} pr-12`}
                data-testid="reset-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"
              >
                {showPassword ? <EyeOff size={22} /> : <Eye size={22} />}
              </button>
            </div>
            <p className="text-stone-500 text-sm">Must be at least 6 characters.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-stone-700 text-base">Confirm password</Label>
            <Input
              id="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={inputCls}
              data-testid="reset-confirm-password-input"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#EFB342] text-black hover:bg-[#D99F30] font-bold uppercase tracking-wider h-14 text-base lg:text-lg rounded-full"
            data-testid="reset-submit-btn"
          >
            {loading ? <Loader2 className="animate-spin" size={24} /> : 'Reset password'}
          </Button>
        </form>

        <p className="mt-8 text-center text-stone-500 text-base">
          Remember your password?{' '}
          <Link to="/login" className="text-[#B8871E] hover:underline font-semibold">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
